export const CSV_HEADER = 'Datum;ph;cl;ta;ph+;ph-;chlor;robi;filter;notiz';
/** Frühere Exporte ohne Notiz-Spalte bleiben importierbar. */
const LEGACY_HEADER = 'Datum;ph;cl;ta;ph+;ph-;chlor;robi;filter';
const BOM = '\uFEFF';
/** Spiegelt MaxNoteLength in Poolmanager.Api/Validation/RequestValidation.cs. */
const MAX_NOTE_LENGTH = 2000;
/** Spalte im Export/Import je Dosiermittel. */
const AGENT_COLUMN = { phPlus: 4, phMinus: 5, chlor: 6 };
/** Sortierreihenfolge der Typen innerhalb eines Tages im Export. */
const TYPE_ORDER = ['messung', 'filter', 'robot', 'dose', 'note'];
/** Dezimalkomma statt Punkt, volle gespeicherte Präzision (keine Rundung). */
function formatNum(v) {
    return v === null ? '' : String(v).replace('.', ',');
}
/** Excel-konform quoten, falls der Wert Trennzeichen, Quotes oder Umbrüche enthält. */
function quoteField(v) {
    return /[";\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
}
export function toCsv(measurements, careEntries) {
    const rows = [];
    for (const m of measurements) {
        rows.push({
            typ: 'messung',
            date: m.date,
            id: m.id,
            fields: [formatNum(m.ph), formatNum(m.cl), formatNum(m.ta), '', '', '', '', '', '']
        });
    }
    for (const c of careEntries) {
        const fields = ['', '', '', '', '', '', '', '', ''];
        if (c.type === 'dose' && c.agent !== null) {
            fields[AGENT_COLUMN[c.agent] - 1] = formatNum(c.grams);
        }
        else if (c.type === 'robot') {
            fields[6] = 'x';
        }
        else if (c.type === 'filter') {
            fields[7] = 'x';
        }
        else if (c.type === 'note' && !c.note) {
            continue; // leere Tagesnotiz kann es regulär nicht geben
        }
        fields[8] = quoteField(c.note ?? '');
        rows.push({ typ: c.type, date: c.date, id: c.id, fields });
    }
    rows.sort((a, b) => a.date.localeCompare(b.date) ||
        TYPE_ORDER.indexOf(a.typ) - TYPE_ORDER.indexOf(b.typ) ||
        a.id - b.id);
    const lines = rows.map(r => [r.date, ...r.fields].join(';'));
    return BOM + [CSV_HEADER, ...lines].join('\r\n') + '\r\n';
}
/**
 * Zerlegt CSV-Text quote-aware in Records (Excel quotet Felder je nach
 * Inhalt selbständig - split('\n') reicht nicht).
 */
function splitRecords(text) {
    const records = [];
    let fields = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else {
                    inQuotes = false;
                }
            }
            else {
                field += ch;
            }
        }
        else if (ch === '"') {
            inQuotes = true;
        }
        else if (ch === ';') {
            fields.push(field);
            field = '';
        }
        else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text[i + 1] === '\n')
                i++;
            fields.push(field);
            records.push(fields);
            fields = [];
            field = '';
        }
        else {
            field += ch;
        }
    }
    // Letzter Record ohne abschließenden Zeilenumbruch
    if (field !== '' || fields.length > 0) {
        fields.push(field);
        records.push(fields);
    }
    // Komplett leere Records (Leerzeilen) überspringen
    return records.filter(r => r.length > 1 || r[0] !== '');
}
/** Akzeptiert Dezimalkomma und -punkt. Liefert null bei nicht parsbarem Wert. */
function parseNum(raw) {
    const n = Number(raw.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
}
function isValidDate(s) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s))
        return false;
    const d = new Date(s + 'T12:00:00');
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
function matchesHeader(header, expected) {
    const fields = expected.toLowerCase().split(';');
    return header.length === fields.length && header.every((f, i) => f.trim().toLowerCase() === fields[i]);
}
export function parseCsv(text) {
    const records = splitRecords(text.startsWith(BOM) ? text.slice(BOM.length) : text);
    const err = (recordNo, message) => ({
        ok: false,
        error: `Zeile ${recordNo}: ${message}`
    });
    const header = records[0];
    if (!header || !(matchesHeader(header, CSV_HEADER) || matchesHeader(header, LEGACY_HEADER))) {
        return err(1, `Ungültige Kopfzeile, erwartet "${CSV_HEADER}".`);
    }
    const columnCount = header.length;
    const measurements = [];
    const careEntries = [];
    for (let r = 1; r < records.length; r++) {
        const no = r + 1; // Kopfzeile ist Zeile 1
        const rec = records[r];
        if (rec.length !== columnCount)
            return err(no, `Erwartet ${columnCount} Spalten, gefunden ${rec.length}.`);
        const [datum, phRaw, clRaw, taRaw, phPlusRaw, phMinusRaw, chlorRaw, robiRaw, filterRaw, notizRaw] = rec.map(f => f.trim());
        const notiz = notizRaw ?? ''; // Legacy-Format ohne Notiz-Spalte
        if (!isValidDate(datum))
            return err(no, `Ungültiges Datum "${datum}" (erwartet JJJJ-MM-TT).`);
        // Messwerte (ph/cl/ta): mindestens einer gesetzt => eine Messung
        const values = [];
        for (const [label, raw, min, max, unit] of [
            ['pH-Wert', phRaw, 0, 14, ''],
            ['Chlorwert', clRaw, 0, 10, ' mg/l'],
            ['Alkalinität', taRaw, 0, 1000, ' ppm']
        ]) {
            if (raw === '') {
                values.push(null);
                continue;
            }
            const n = parseNum(raw);
            if (n === null)
                return err(no, `${label} "${raw}" ist keine Zahl.`);
            if (n < min || n > max)
                return err(no, `${label} muss zwischen ${min} und ${max}${unit} liegen.`);
            values.push(n);
        }
        const [ph, cl, ta] = values;
        let hasEntry = false;
        if (ph !== null || cl !== null || ta !== null) {
            measurements.push({ date: datum, ph, cl, ta });
            hasEntry = true;
        }
        // Dosierungen: gefüllte Mittel-Spalte => ein Dosier-Eintrag mit der Grammzahl
        const careCountBefore = careEntries.length;
        for (const [agent, label, raw] of [
            ['phPlus', 'ph+', phPlusRaw],
            ['phMinus', 'ph-', phMinusRaw],
            ['chlor', 'chlor', chlorRaw]
        ]) {
            if (raw === '')
                continue;
            const grams = parseNum(raw);
            if (grams === null || grams <= 0 || grams > 10000)
                return err(no, `Grammzahl in Spalte "${label}" muss über 0 und höchstens 10000 sein.`);
            careEntries.push({ type: 'dose', date: datum, agent, grams, note: null });
            hasEntry = true;
        }
        // Robi/Filter: "x" => ein Pflege-Eintrag
        for (const [type, label, raw] of [
            ['robot', 'robi', robiRaw],
            ['filter', 'filter', filterRaw]
        ]) {
            if (raw === '')
                continue;
            if (raw.toLowerCase() !== 'x')
                return err(no, `In Spalte "${label}" ist nur "x" erlaubt.`);
            careEntries.push({ type, date: datum, agent: null, grams: null, note: null });
            hasEntry = true;
        }
        // Notiz: gehört zum Pflege-Eintrag der Zeile; ohne einen solchen ist sie
        // eine eigenständige Tagesnotiz (so legt sie auch das Mess-Sheet an).
        if (notiz !== '') {
            if (notiz.length > MAX_NOTE_LENGTH)
                return err(no, `Notiz darf höchstens ${MAX_NOTE_LENGTH} Zeichen lang sein.`);
            const rowCareCount = careEntries.length - careCountBefore;
            if (rowCareCount > 1)
                return err(no, 'Notiz ist nicht eindeutig zuordenbar: Zeile enthält mehrere Pflege-Einträge (Zeile aufteilen).');
            if (rowCareCount === 1) {
                careEntries[careCountBefore].note = notiz;
            }
            else {
                careEntries.push({ type: 'note', date: datum, agent: null, grams: null, note: notiz });
            }
            hasEntry = true;
        }
        if (!hasEntry)
            return err(no, 'Zeile enthält keine Eintragung.');
    }
    return { ok: true, measurements, careEntries };
}
